import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Icon,
  Skeleton,
  SkeletonText,
  Tag,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { RiFlaskLine, RiInformationLine, RiMagicLine } from "react-icons/ri";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import { MaybeLink } from "../Links/MaybeLink";
import { TipTag } from "../TipTag";

export type IdentityIntelligenceAlertProps = {
  isEnabled: boolean;
  isLoading: boolean;
  hasError: boolean;
  hasMatches: boolean;
};

export const IdentityIntelligenceAlert: React.FC<
  IdentityIntelligenceAlertProps
> = ({ isEnabled, isLoading, hasError, hasMatches }) => {
  const organization = useOrganization();
  const avelaLabsUrl = organization
    .map((org) => Url.OrgAdmin.AvelaLabs.aboutAvelaLabs(org))
    .withDefault(undefined);

  if (!isEnabled) {
    return null;
  }

  return (
    <Alert
      status="info"
      borderRadius="md"
      mb={4}
      bg="purple.50"
      borderColor="purple.500"
      borderWidth="1px"
      borderStyle="solid"
      position="relative"
    >
      <Flex direction="row" gap={2} w="100%" align="center">
        <AlertIcon as={RiMagicLine} color="purple.500" boxSize={6} mr={3} />

        <Box flex="1">
          <AlertTitle
            fontSize="16px"
            fontWeight="700"
            color="gray.800"
            lineHeight="24px"
          >
            {isLoading ? (
              <Skeleton height="20px" width="220px" />
            ) : hasError ? (
              "Avela Intelligence couldn't process this document"
            ) : hasMatches ? (
              "Avela Intelligence found something"
            ) : (
              "Avela Intelligence couldn't find any exact matches"
            )}
          </AlertTitle>
          <AlertDescription
            fontSize="16px"
            color="gray.700"
            mt={1}
            lineHeight="24px"
          >
            {isLoading ? (
              <SkeletonText
                mt="2"
                noOfLines={1}
                spacing="4"
                skeletonHeight="4"
              />
            ) : hasError ? (
              "Avela Intelligence could not scan this document. It may be an unsupported file type, such as a PDF or HEIC/HEIF file."
            ) : hasMatches ? (
              "Avela Intelligence has scanned this document and found matching information with high confidence."
            ) : (
              "Avela Intelligence has scanned this document but didn't find any exact matches with form or profile information."
            )}
          </AlertDescription>
        </Box>

        <Flex direction="column" align="flex-end">
          <Tooltip
            label={
              <Flex p={2} gap={3} direction="column">
                <TipTag />
                <Text fontWeight="semibold" fontSize="md">
                  Avela Intelligence
                </Text>
                <Text fontSize="sm" lineHeight="1.4">
                  Avela Intelligence will review documents for this verification
                  and find matching information. When matches are found with
                  high confidence, they are highlighted on the document and
                  summarized next to the matching fields.
                </Text>
              </Flex>
            }
            placement="top-end"
            hasArrow
            bg="white"
            color="gray.800"
            borderRadius="lg"
            shadow="lg"
          >
            <Box
              as="button"
              type="button"
              mt={1}
              flexShrink={0}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
              aria-label="Information about identity verification"
            >
              <Icon as={RiInformationLine} boxSize={5} color="purple.800" />
            </Box>
          </Tooltip>
          <Flex direction="row" gap={1} align="center" pt={4}>
            <MaybeLink to={avelaLabsUrl} isExternal>
              <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
                <Icon as={RiFlaskLine} />
                <Text>Beta</Text>
              </Tag>
            </MaybeLink>
          </Flex>
        </Flex>
      </Flex>
    </Alert>
  );
};
